import type {
  AzureBlobConfig,
  AzureFilesConfig,
  BackBlazeConfig,
  BoxConfig,
  CloudinaryConfig,
  DrimeConfig,
  FichierConfig,
  FileluConfig,
  FilesComConfig,
  FS,
  FSConfig,
  FSType,
  FtpConfig,
  GcsConfig,
  GofileConfig,
  HttpConfig,
  ImagekitConfig,
  InternetArchiveConfig,
  JottacloudConfig,
  KoofrConfig,
  LinkboxConfig,
  LocalConfig,
  MegaConfig,
  OpendriveConfig,
  PcloudConfig,
  PixeldrainConfig,
  PremiumizeConfig,
  PutioConfig,
  QuatrixConfig,
  S3Config,
  SeafileConfig,
  SftpConfig,
  SmbConfig,
  StorjConfig,
  SwiftConfig,
  UloztoConfig,
  WebdavConfig,
  YandexConfig,
} from "./fs";
import {
  fsTypeAzureBlob,
  fsTypeAzureFiles,
  fsTypeBackBlaze,
  fsTypeBox,
  fsTypeCloudinary,
  fsTypeDrime,
  fsTypeDropbox,
  fsTypeFichier,
  fsTypeFilelu,
  fsTypeFilesCom,
  fsTypeFromFSConfig,
  fsTypeFtp,
  fsTypeGcs,
  fsTypeGDrive,
  fsTypeGofile,
  fsTypeHttp,
  fsTypeImagekit,
  fsTypeInternetArchive,
  fsTypeJottacloud,
  fsTypeKoofr,
  fsTypeLinkbox,
  fsTypeMega,
  fsTypeOneDrive,
  fsTypeOpendrive,
  fsTypePcloud,
  fsTypePixeldrain,
  fsTypePremiumize,
  fsTypePutio,
  fsTypeQuatrix,
  fsTypeS3,
  fsTypeSeafile,
  fsTypeSftp,
  fsTypeSmb,
  fsTypeStorj,
  fsTypeSwift,
  fsTypeUlozto,
  fsTypeWebdav,
  fsTypeYandex,
} from "./fs";
import { makeFileSystemAzureBlob } from "./fs-azureblob";
import { makeFileSystemAzureFiles } from "./fs-azurefiles";
import { makeFileSystemBackBlaze } from "./fs-backblaze";
import { makeFileSystemBox } from "./fs-box";
import { makeFileSystemCloudinary } from "./fs-cloudinary";
import { makeFileSystemDrime } from "./fs-drime";
import { connectDropbox } from "./fs-dropbox-login";
import { makeFileSystemFichier } from "./fs-fichier";
import { makeFileSystemFilelu } from "./fs-filelu";
import { makeFileSystemFilesCom } from "./fs-filescom";
import { makeFileSystemFtp } from "./fs-ftp";
import { makeFileSystemGcs } from "./fs-gcs";
import { connectGDrive, makeFileSystemGDrive } from "./fs-gdrive";
import { makeFileSystemGofile } from "./fs-gofile";
import { makeFileSystemHttp } from "./fs-http";
import { makeFileSystemImagekit } from "./fs-imagekit";
import { makeFileSystemInternetArchive } from "./fs-internetarchive";
import { makeFileSystemJottacloud } from "./fs-jottacloud";
import { makeFileSystemKoofr } from "./fs-koofr";
import { makeFileSystemLinkbox } from "./fs-linkbox";
import { makeFileSystemLocal } from "./fs-local";
import { makeFileSystemMega } from "./fs-mega";
import { makeFileSystemOneDrive } from "./fs-onedrive";
import { connectOneDrive } from "./fs-onedrive-login";
import { makeFileSystemOpendrive } from "./fs-opendrive";
import { makeFileSystemPcloud } from "./fs-pcloud";
import { makeFileSystemPixeldrain } from "./fs-pixeldrain";
import { makeFileSystemPremiumize } from "./fs-premiumize";
import { makeFileSystemPutio } from "./fs-putio";
import { makeFileSystemQuatrix } from "./fs-quatrix";
import { makeFileSystemS3 } from "./fs-s3";
import { makeFileSystemSeafile } from "./fs-seafile";
import { makeFileSystemSftp } from "./fs-sftp";
import { makeFileSystemSmb } from "./fs-smb";
import { makeFileSystemStorj } from "./fs-storj";
import { makeFileSystemSwift } from "./fs-swift";
import { makeFileSystemUlozto } from "./fs-ulozto";
import { makeFileSystemWebdav } from "./fs-webdav";
import { makeFileSystemYandex } from "./fs-yandex";
import { ensureLocalPermission, loadLocalHandle } from "./local-handles";

// Dropbox, OneDrive and Google Drive are connected by signing in, and there
// is one connection of each: its id is the type, e.g. "dropbox:".
export function fsTypeNeedsSignIn(fsType: FSType): boolean {
  return fsType == fsTypeDropbox || fsType == fsTypeOneDrive || fsType == fsTypeGDrive;
}

export function signInFsName(fsType: FSType): string {
  switch (fsType) {
    case fsTypeDropbox:
      return "Dropbox";
    case fsTypeOneDrive:
      return "OneDrive";
    case fsTypeGDrive:
      return "Google Drive";
  }
  return fsType;
}

// Opens the sign-in of Dropbox, OneDrive or Google Drive and returns the file
// system once the user has signed in. Must be called from a click or a key
// press: the sign-in is a window, which the browser blocks otherwise.
export async function connectBySignIn(fsType: FSType): Promise<FS> {
  switch (fsType) {
    case fsTypeDropbox:
      return new Promise((resolve, reject) => {
        connectDropbox(resolve).catch(reject);
      });
    case fsTypeOneDrive:
      return makeFileSystemOneDrive(await connectOneDrive());
    case fsTypeGDrive:
      return makeFileSystemGDrive(await connectGDrive());
  }
  throw new Error("unsupported fsType " + fsType);
}

// Opens the local folder of a saved connection: gets its handle back and
// asks the user to let us use the folder again, which the browser wants after
// every reload. Must be called from a click or a key press.
export async function openSavedLocalFolder(saved: FSConfig): Promise<FS> {
  let handle = await loadLocalHandle(saved.fsID);
  if (!handle) {
    throw new Error("This folder is not remembered by the browser any more. Connect it again with Local Folder.");
  }
  if (!(await ensureLocalPermission(handle))) {
    throw new Error("The browser was not allowed to use the folder");
  }
  let c: LocalConfig = { fsID: saved.fsID, name: saved.name, dirHandle: handle };
  return makeFileSystemLocal(c);
}

// Makes the file system for a saved connection. Connections that are not
// saved between sessions (Dropbox, OneDrive, Google Drive, a local folder)
// cannot be made this way: they need a new sign-in, or, for a local folder,
// openSavedLocalFolder().
export function makeFileSystemFromConfig(c: FSConfig): FS {
  let fsType = fsTypeFromFSConfig(c);
  switch (fsType) {
    case fsTypeS3:
      return makeFileSystemS3(c as S3Config);
    case fsTypeBackBlaze:
      return makeFileSystemBackBlaze(c as BackBlazeConfig);
    case fsTypePutio:
      return makeFileSystemPutio(c as PutioConfig);
    case fsTypePixeldrain:
      return makeFileSystemPixeldrain(c as PixeldrainConfig);
    case fsTypeStorj:
      return makeFileSystemStorj(c as StorjConfig);
    case fsTypeSftp:
      return makeFileSystemSftp(c as SftpConfig);
    case fsTypeFtp:
      return makeFileSystemFtp(c as FtpConfig);
    case fsTypeSmb:
      return makeFileSystemSmb(c as SmbConfig);
    case fsTypeWebdav:
      return makeFileSystemWebdav(c as WebdavConfig);
    case fsTypeBox:
      return makeFileSystemBox(c as BoxConfig);
    case fsTypeAzureBlob:
      return makeFileSystemAzureBlob(c as AzureBlobConfig);
    case fsTypeGcs:
      return makeFileSystemGcs(c as GcsConfig);
    case fsTypePcloud:
      return makeFileSystemPcloud(c as PcloudConfig);
    case fsTypeMega:
      return makeFileSystemMega(c as MegaConfig);
    case fsTypeKoofr:
      return makeFileSystemKoofr(c as KoofrConfig);
    case fsTypeSeafile:
      return makeFileSystemSeafile(c as SeafileConfig);
    case fsTypeYandex:
      return makeFileSystemYandex(c as YandexConfig);
    case fsTypeJottacloud:
      return makeFileSystemJottacloud(c as JottacloudConfig);
    case fsTypeSwift:
      return makeFileSystemSwift(c as SwiftConfig);
    case fsTypeFichier:
      return makeFileSystemFichier(c as FichierConfig);
    case fsTypeOpendrive:
      return makeFileSystemOpendrive(c as OpendriveConfig);
    case fsTypeGofile:
      return makeFileSystemGofile(c as GofileConfig);
    case fsTypePremiumize:
      return makeFileSystemPremiumize(c as PremiumizeConfig);
    case fsTypeInternetArchive:
      return makeFileSystemInternetArchive(c as InternetArchiveConfig);
    case fsTypeAzureFiles:
      return makeFileSystemAzureFiles(c as AzureFilesConfig);
    case fsTypeHttp:
      return makeFileSystemHttp(c as HttpConfig);
    case fsTypeFilesCom:
      return makeFileSystemFilesCom(c as FilesComConfig);
    case fsTypeFilelu:
      return makeFileSystemFilelu(c as FileluConfig);
    case fsTypeImagekit:
      return makeFileSystemImagekit(c as ImagekitConfig);
    case fsTypeCloudinary:
      return makeFileSystemCloudinary(c as CloudinaryConfig);
    case fsTypeDrime:
      return makeFileSystemDrime(c as DrimeConfig);
    case fsTypeQuatrix:
      return makeFileSystemQuatrix(c as QuatrixConfig);
    case fsTypeLinkbox:
      return makeFileSystemLinkbox(c as LinkboxConfig);
    case fsTypeUlozto:
      return makeFileSystemUlozto(c as UloztoConfig);
  }
  throw new Error("unsupported fsType " + fsType);
}
